import { AlertCircle, AlertTriangle, Plus, RefreshCw } from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaAlert } from "@/shared/components/feedback/ColocaAlert";
import { ColocaSkeleton } from "@/shared/components/feedback/ColocaSkeleton";
import { ColocaPageHeader } from "@/shared/components/layout/ColocaPageHeader";
import { useAppShellBreadcrumb } from "@/shared/components/layout/useAppShellBreadcrumb";
import { ColocaTabs, type ColocaTabItem } from "@/shared/components/navigation/ColocaTabs";
import { NoAccessView } from "@/shared/components/NoAccessView";
import { useIsMobile } from "@/shared/hooks/use-mobile";
import useTranslations from "@/shared/lang/useTranslations";
import { cn } from "@/shared/lib/utils";
import { formatDateTime } from "@/shared/utils/dateTimeHelpers";
import { colocaToast } from "@/shared/utils/toast";

import { CorridorCoverageSection, type CorridorActions } from "../components/fees/CorridorCoverageSection";
import { CorridorHistoryDrawer } from "../components/fees/CorridorHistoryDrawer";
import { DeleteFeeDialog } from "../components/fees/DeleteFeeDialog";
import {
  EditCorridorPriceDialog,
  type EditCorridorPriceIntent,
  type EditCorridorPriceSaved,
} from "../components/fees/EditCorridorPriceDialog";
import { ExampleAmountsBar } from "../components/fees/ExampleAmountsBar";
import { FeeChangesFeed } from "../components/fees/FeeChangesFeed";
import { FeeFilters } from "../components/fees/FeeFilters";
import { FeeKpiStrip } from "../components/fees/FeeKpiStrip";
import { FeeRuleGroupList } from "../components/fees/FeeRuleGroupList";
import { FeesHowItWorksGuide } from "../components/fees/FeesHowItWorksGuide";
import { RemoveSpecialPriceDialog } from "../components/fees/RemoveSpecialPriceDialog";
import { ScopeBanner } from "../components/fees/ScopeBanner";
import { TabIntro } from "../components/fees/TabIntro";
import { OrganizationScopePicker } from "../components/OrganizationScopePicker";
import { useFeeLabels } from "../hooks/useFeeLabels";
import { useOrganizationNames } from "../hooks/useOrganizationNames";
import { useOrganizationsCatalog } from "../hooks/useOrganizationsCatalog";
import { usePaymentFees } from "../hooks/usePaymentFees";
import { useFeeAuthors, useRoutesCatalog } from "../hooks/usePaymentFeesCatalogs";
import { usePaymentTaxes } from "../hooks/usePaymentTaxes";
import {
  PaymentFeeKind,
  type CorridorCoverage,
  type FeeTabId,
  type PaymentFee,
} from "../types/paymentFees.types";
import { corridorLabel, shortId } from "../utils/feeFormat";

/**
 * `/app/payments-config/fees` — the price book, for INTERNAL staff. The scope picker decides both what is on
 * screen and what a change reaches: the general price, or one organization's special price.
 */
const PaymentFeesPage = () => {
  const { t, activeLanguage } = useTranslations("paymentFees");
  const { t: tGlobal } = useTranslations("global");
  const labels = useFeeLabels();
  const isMobile = useIsMobile();

  useAppShellBreadcrumb(
    useMemo(
      () => [
        { label: tGlobal("sidebar_section_gestion") },
        { label: tGlobal("sidebar_menu_payment_fees"), to: "/app/payments-config/fees" },
      ],
      [tGlobal],
    ),
  );

  const routesCatalog = useRoutesCatalog();
  const organizationsCatalog = useOrganizationsCatalog();
  const { nameById: authorNames } = useFeeAuthors();
  const taxesState = usePaymentTaxes();
  const feesState = usePaymentFees(routesCatalog.routes, taxesState.rows);
  const scopeOrganizationId = feesState.scopeOrganizationId;

  const [deleteTarget, setDeleteTarget] = useState<PaymentFee | null>(null);
  const [editIntent, setEditIntent] = useState<EditCorridorPriceIntent | null>(null);
  const [removeTarget, setRemoveTarget] = useState<CorridorCoverage | null>(null);
  const [historyTarget, setHistoryTarget] = useState<CorridorCoverage | null>(null);

  const organizationIds = useMemo(
    () => [scopeOrganizationId, ...new Set(feesState.fees.map((fee) => fee.organization_id))],
    [scopeOrganizationId, feesState.fees],
  );
  const organizationNames = useOrganizationNames(organizationsCatalog.organizations, organizationIds);
  const scopeOrganizationName = scopeOrganizationId
    ? (organizationNames[scopeOrganizationId] ?? shortId(scopeOrganizationId))
    : null;

  const scopeLabels = useMemo(
    () => ({
      label: t("scope_label"),
      placeholder: t("scope_placeholder"),
      loading: t("scope_loading"),
      empty: t("scope_empty"),
      searching: t("scope_searching"),
      searchError: t("scope_search_error"),
      catalogError: t("scope_catalog_error"),
      catalogTruncated: t("scope_catalog_truncated"),
      globalOption: t("scope_global_option"),
      globalHint: t("scope_global_hint"),
      orgHint: t("scope_org_hint"),
    }),
    [t],
  );

  const currencies = useMemo(
    () => [...new Set(routesCatalog.routes.map((route) => route.currency))].sort(),
    [routesCatalog.routes],
  );

  const refresh = feesState.refresh;
  const navigate = useNavigate();

  const openRule = useCallback(
    (mode: "new" | "version" | "duplicate", feeKind: PaymentFeeKind, source?: PaymentFee) => {
      const params = new URLSearchParams({ mode, kind: feeKind });
      if (source) params.set("source", source.id);
      if (scopeOrganizationId) params.set("organization_id", scopeOrganizationId);
      navigate(`/app/payments-config/fees/rules/new?${params.toString()}`);
    },
    [navigate, scopeOrganizationId],
  );

  const handleEditSaved = useCallback(
    (saved: EditCorridorPriceSaved) => {
      const name = editIntent?.organizationName ?? "";
      setEditIntent(null);
      if (saved.validFrom) {
        colocaToast.success(
          t("toast_scheduled", { date: formatDateTime(saved.validFrom, { locale: activeLanguage }) }),
        );
      } else if (saved.role === "SPECIAL") {
        colocaToast.success(t("toast_saved_special", { name }));
      } else {
        colocaToast.success(t(saved.role === "COST" ? "toast_saved_cost" : "toast_saved_general"));
      }
      void refresh();
    },
    [editIntent, refresh, t, activeLanguage],
  );

  const handleRemoved = useCallback(() => {
    if (removeTarget && scopeOrganizationName) {
      colocaToast.success(
        t("toast_removed", {
          name: scopeOrganizationName,
          corridor: corridorLabel(removeTarget.route, labels.directionShort),
        }),
      );
    }
    setRemoveTarget(null);
    void refresh();
  }, [removeTarget, scopeOrganizationName, labels, refresh, t]);

  const handleDeleted = useCallback(
    (wasMissing: boolean) => {
      const version = deleteTarget?.version;
      setDeleteTarget(null);
      if (wasMissing) colocaToast.info(t("error_delete_missing"));
      else if (version != null) colocaToast.success(t("toast_deleted", { version }));
      void refresh();
    },
    [deleteTarget, refresh, t],
  );

  const corridorActions = useMemo<CorridorActions>(
    () => ({
      onEditCommission: (coverage) =>
        setEditIntent({
          coverage,
          feeKind: PaymentFeeKind.COLOCA_COMMISSION,
          organizationId: scopeOrganizationId,
          organizationName: scopeOrganizationName,
        }),
      onEditCost: (coverage) =>
        setEditIntent({
          coverage,
          feeKind: PaymentFeeKind.PROVIDER_COST,
          organizationId: null,
          organizationName: null,
        }),
      onBackToGeneral: setRemoveTarget,
      onHistory: setHistoryTarget,
      onViewRules: (coverage, kind) =>
        feesState.focusCorridor(
          coverage.route,
          kind === PaymentFeeKind.PROVIDER_COST ? "costs" : "commissions",
        ),
      onViewOrganization: feesState.setScopeOrganizationId,
    }),
    [scopeOrganizationId, scopeOrganizationName, feesState],
  );

  if (!feesState.hasAccess) return <NoAccessView />;

  const tabs: ColocaTabItem[] = [
    { value: "corridors", label: t("tab_corridors"), content: null },
    { value: "history", label: t("tab_history"), content: null },
    { value: "commissions", label: t("tab_commissions"), content: null },
    { value: "costs", label: t("tab_costs"), content: null },
  ];
  const ruleTab = feesState.tab === "commissions" || feesState.tab === "costs" ? feesState.tab : null;
  const routesUnavailable = routesCatalog.hasError || routesCatalog.accessDenied;

  return (
    <div className="space-y-6 p-2">
      <ColocaPageHeader
        title={t("page_title")}
        description={t("page_subtitle")}
        actions={
          <>
            <ColocaButton
              variant="default"
              size="sm"
              icon={RefreshCw}
              aria-label={t("refresh_aria")}
              className={cn(feesState.isRefreshing && "[&_svg]:animate-spin")}
              onClick={() => void refresh()}
            >
              {t("refresh_button")}
            </ColocaButton>
            <ColocaButton
              variant="default"
              size="sm"
              icon={Plus}
              onClick={() =>
                openRule(
                  "new",
                  feesState.tab === "costs" && !scopeOrganizationId
                    ? PaymentFeeKind.PROVIDER_COST
                    : PaymentFeeKind.COLOCA_COMMISSION,
                )
              }
            >
              {t("button_new_fee")}
            </ColocaButton>
          </>
        }
      />

      <FeesHowItWorksGuide />

      <div className="space-y-3">
        <OrganizationScopePicker
          id="payment-fees-scope"
          className="max-w-md"
          value={scopeOrganizationId}
          onValueChange={feesState.setScopeOrganizationId}
          catalog={organizationsCatalog}
          labels={scopeLabels}
        />
        <ScopeBanner
          organizationName={scopeOrganizationName}
          specialPriceOrganizations={feesState.organizationsWithSpecialPrice.length}
          onBackToGeneral={() => feesState.setScopeOrganizationId(null)}
        />
      </div>

      {feesState.truncated && (
        <ColocaAlert
          variant="warning"
          icon={AlertTriangle}
          text={t("truncated_notice", { loaded: feesState.loadedCount, total: feesState.totalCount })}
          actions={
            <ColocaButton variant="default" size="sm" icon={RefreshCw} onClick={feesState.loadAll}>
              {t("truncated_load_all")}
            </ColocaButton>
          }
        />
      )}

      {feesState.hasError ? (
        <ColocaAlert
          variant="destructive"
          icon={AlertCircle}
          heading={t("error_load")}
          text={t("error_load_detail")}
          actions={
            <ColocaButton variant="default" size="sm" icon={RefreshCw} onClick={() => void refresh()}>
              {t("error_retry")}
            </ColocaButton>
          }
        />
      ) : (
        <>
          <ColocaTabs
            tabs={tabs}
            variant="line"
            value={feesState.tab}
            onValueChange={(value) => feesState.setTab(value as FeeTabId)}
            listClassName="overflow-x-auto"
          />

          <TabIntro tab={feesState.tab} />

          {feesState.tab === "corridors" && (
            <div className="space-y-4">
              {feesState.isLoading ? (
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-5">
                  {[0, 1, 2, 3, 4].map((index) => (
                    <ColocaSkeleton key={index} className="h-[5.5rem] rounded-xl" />
                  ))}
                </div>
              ) : (
                <FeeKpiStrip
                  kpis={feesState.corridorKpis}
                  filter={feesState.corridorFilter}
                  onFilterChange={feesState.setCorridorFilter}
                  scoped={scopeOrganizationId != null}
                  unavailable={routesUnavailable}
                />
              )}
              <ExampleAmountsBar
                currencies={currencies}
                exampleAmountFor={feesState.exampleAmountFor}
                onChange={feesState.setExampleAmount}
                onReset={feesState.resetExampleAmount}
              />
              <CorridorCoverageSection
                corridors={feesState.visibleCorridors}
                totalCount={feesState.corridors.length}
                isLoading={feesState.isLoading || routesCatalog.isLoading}
                routesError={routesUnavailable}
                onRetryRoutes={routesCatalog.refresh}
                scopeOrganizationId={scopeOrganizationId}
                organizationNames={organizationNames}
                {...corridorActions}
              />
              <ColocaAlert variant="secondary" icon={AlertCircle} text={t("why_banner")} />
            </div>
          )}

          {feesState.tab === "history" && (
            <FeeChangesFeed
              fees={feesState.fees}
              now={feesState.now}
              routes={routesCatalog.routes}
              isLoading={feesState.isLoading}
              scopeOrganizationId={scopeOrganizationId}
              exampleAmountFor={feesState.exampleAmountFor}
              organizationNames={organizationNames}
              authorNames={authorNames}
            />
          )}

          {ruleTab && (
            <div className="space-y-4">
              <FeeFilters
                tab={ruleTab}
                filters={feesState.filters}
                routes={routesCatalog.routes}
                onCorridorChange={feesState.setCorridor}
                onDirectionChange={feesState.setDirection}
                onProviderCodeChange={feesState.setProviderCode}
                onShowSupersededChange={feesState.setShowSuperseded}
                onShowScheduledChange={feesState.setShowScheduled}
                onClear={feesState.clearFilters}
                hasActiveFilters={feesState.hasActiveFilters}
                activeFilterCount={feesState.activeFilterCount}
                isMobile={isMobile}
              />
              <FeeRuleGroupList
                groups={feesState.groups}
                fees={feesState.fees}
                now={feesState.now}
                exampleAmountFor={feesState.exampleAmountFor}
                routes={routesCatalog.routes}
                authorNames={authorNames}
                organizationNames={organizationNames}
                isLoading={feesState.isLoading}
                expandedKeys={feesState.expandedKeys}
                onToggle={feesState.toggleGroup}
                highlightedKey={feesState.highlightedKey}
                onNewVersion={(fee) => openRule("version", fee.fee_kind, fee)}
                onDuplicate={(fee) => openRule("duplicate", fee.fee_kind, fee)}
                onDelete={setDeleteTarget}
                hasActiveFilters={feesState.hasActiveFilters}
                onClearFilters={feesState.clearFilters}
                onCreateFirst={() =>
                  openRule(
                    "new",
                    ruleTab === "costs" ? PaymentFeeKind.PROVIDER_COST : PaymentFeeKind.COLOCA_COMMISSION,
                  )
                }
                emptyTitle={t(ruleTab === "costs" ? "empty_costs_title" : "empty_commissions_title")}
                generalOnlyNote={ruleTab === "commissions" && scopeOrganizationId == null}
              />
            </div>
          )}
        </>
      )}

      {editIntent && (
        <EditCorridorPriceDialog
          intent={editIntent}
          open
          onClose={() => setEditIntent(null)}
          onSaved={handleEditSaved}
          routes={routesCatalog.routes}
          fees={feesState.fees}
          now={feesState.now}
          exampleAmountFor={feesState.exampleAmountFor}
          organizationNames={organizationNames}
        />
      )}

      {removeTarget && scopeOrganizationId && (
        <RemoveSpecialPriceDialog
          route={removeTarget.route}
          organizationId={scopeOrganizationId}
          organizationName={scopeOrganizationName ?? scopeOrganizationId}
          open
          onClose={() => {
            setRemoveTarget(null);
            void refresh();
          }}
          onRemoved={handleRemoved}
          routes={routesCatalog.routes}
          fees={feesState.fees}
          now={feesState.now}
        />
      )}

      {historyTarget && (
        <CorridorHistoryDrawer
          route={historyTarget.route}
          organizationId={scopeOrganizationId}
          organizationName={scopeOrganizationName}
          open
          onClose={() => setHistoryTarget(null)}
          fees={feesState.fees}
          now={feesState.now}
          exampleAmountFor={feesState.exampleAmountFor}
          organizationNames={organizationNames}
          authorNames={authorNames}
        />
      )}

      {deleteTarget && (
        <DeleteFeeDialog
          fee={deleteTarget}
          open
          onClose={() => setDeleteTarget(null)}
          onDeleted={handleDeleted}
          fees={feesState.fees}
          routes={routesCatalog.routes}
          organizationNames={organizationNames}
        />
      )}
    </div>
  );
};

export default PaymentFeesPage;
